import { truthy } from '@avalara/bat-apps-shared/helpers/index';
import { isEmailValid } from '@avalara/bat-apps-shared/lib/form-validation';
import fetch from '../fetch';
import { fetchBillingAccount } from './biztech';

import type { Dispatch, GetState } from '@avalara/types/actions';
import type { BillingContactValidationErrors, UpdateBillingContactOptions } from '@avalara/types/billingContact';
import { validatePhone } from '../../components/helpers/contact-info';
import conf from '../../conf';
import { defaultValidationErrors, receiveBillingContactErrors } from '../../store/billing-contact';
import { getSystemErrorMessaging } from './error';

export const validateBillingContactForm = () => (dispatch: Dispatch, getState: GetState) => {
  const { billingContactEditForm } = getState().billingContact;

  const fieldsToValidate = {
    Address1: { isRequired: true },
    City: { isRequired: true },
    Country: { isRequired: true },
    FirstName: { isRequired: true },
    LastName: { isRequired: true },
    PersonalEmail: { isRequired: false },
    PostalCode: { isRequired: true },
    State: { isRequired: true },
    WorkEmail: { isRequired: true },
    WorkPhone: { isRequired: true },
  };

  const errors = (
    Object.keys(fieldsToValidate) as Array<keyof typeof fieldsToValidate>
  ).reduce<BillingContactValidationErrors>(
    (errorsAccumulator, field) => {
      const value = billingContactEditForm[field];
      const { isRequired } = fieldsToValidate[field];
      const newErrors = Object.assign({}, errorsAccumulator);

      if (isRequired && (value === null || value.trim() === '')) {
        newErrors[field] = 'isRequired';
      }

      if ((field === 'PersonalEmail' || field === 'WorkEmail') && truthy(value) && !isEmailValid(value || '')) {
        newErrors[field] = 'isInvalid';
      }

      if (field === 'WorkPhone' && truthy(value) && !validatePhone(value || '')) {
        newErrors[field] = 'isInvalid';
      }

      return newErrors;
    },
    { ...defaultValidationErrors },
  );

  dispatch(receiveBillingContactErrors(errors));
};

export const createBillingContact =
  ({ accountId, customerAccountId, billingContactData }: UpdateBillingContactOptions) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await fetch(dispatch)(
        `${conf.API_URL}/api/billingContact/a/${accountId}/b/${customerAccountId}/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(billingContactData),
        },
      );

      if (!response.ok) throw new Error(`${response.message}`);

      await fetchBillingAccount(accountId);
    } catch {
      // TODO: Need to define an errorCode for failed create billing contact in src/client/lib/side-effects/error.ts
      dispatch(
        getSystemErrorMessaging({
          errorCode: '',
        }),
      );
    }
  };
